/**
 * 화면 행동 기록에 쓰는 이름 상수 — 오탈자가 "누락"이 아니라 "엉뚱한 칸"으로 쌓이지 않게 여기서만 꺼내 쓴다
 * (docs/unified-log-plan.md 2.3). 서버 사전(`UiEventType`)과 같은 값이어야 한다.
 */

/** 화면 이름 — 서버 `UiEventType.SCREENS` 와 같은 값. 화면 머문 시간(screen_time)도 이 이름으로 센다 */
export const UI_SCREEN = {
    HOME: 'home',
    RECRUIT_LIST: 'recruit_list',
    RECRUIT_DETAIL: 'recruit_detail',
    POST_LIST: 'post_list',
    POST_DETAIL: 'post_detail',
    SEMINAR_LIST: 'seminar_list',
    SEMINAR_DETAIL: 'seminar_detail',
    SEMINAR_PAY_DONE: 'seminar_pay_done',
    EXPERT_LIST: 'expert_list',
    EXPERT_DETAIL: 'expert_detail',
    NOTICE_LIST: 'notice_list',
    NOTICE_DETAIL: 'notice_detail',
    SEARCH: 'search',
    AI_SHEET: 'ai_sheet',
    AI_CHAT: 'ai_chat',
    MY_PAGE: 'my_page',
    RESUME: 'resume',
    APPLY_HISTORY: 'apply_history',
    THREADS: 'threads',
    NOTIFICATIONS: 'notifications',
    CLINIC_RECRUITMENTS: 'clinic_recruitments',
    RECRUIT_FORM: 'recruit_form',
    POST_FORM: 'post_form',
    SALARY_SURVEY: 'salary_survey',
    LOGIN: 'login',
    SIGNUP: 'signup',
    OTHER: 'other',
} as const;

/** 대상 종류 */
export const UI_KIND = {
    RECRUITMENT: 'recruitment',
    CLINIC: 'clinic',
    POST: 'post',
    SEMINAR: 'seminar',
    EXPERT: 'expert',
    SECTION: 'section',
    AI_HOOK: 'ai_hook',
    AI_EXAMPLE: 'ai_example',
    NOTICE: 'notice',
    BANNER: 'banner',
    TAB: 'tab',
    BUTTON: 'button',
} as const;

export type UiScreen = (typeof UI_SCREEN)[keyof typeof UI_SCREEN];
export type UiKind = (typeof UI_KIND)[keyof typeof UI_KIND];

/** 이벤트 종류(서버 UiEventType) */
export const UI_EVENT = {
    IMPRESSION: 'impression',
    CLICK: 'click',
    SEARCH: 'search',
    FILTER_APPLY: 'filter_apply',
    AI_HOOK_VIEW: 'ai_hook_view',
    AI_CHIP_CLICK: 'ai_chip_click',
    AI_QUESTION_SENT: 'ai_question_sent',
    APPLY_START: 'apply_start',
    APPLY_SUBMIT: 'apply_submit',
    SCREEN_TIME: 'screen_time',
    PAY_DONE: 'pay_done',
} as const;

/** 클릭 동작(props.action) — 서버 일 요약이 이 값으로 나눈다 */
export const UI_ACTION = {
    MORE: 'more',
    PAY_START: 'pay_start',
    LIKE: 'like',
    COMMENT: 'comment',
    REPLY: 'reply',
    SHARE: 'share',
} as const;

/**
 * 경로 → 화면 이름(화면 머문 시간용). 쿼리·조각은 보지 않는다. 모르는 경로는 'other'.
 * 순서가 중요하다 — 더 구체적인 경로를 먼저 둔다(`/posts/new` 가 글 상세로 잡히지 않게).
 */
const SCREEN_RULES: Array<[RegExp, UiScreen]> = [
    [/^\/$/, 'home'],
    [/^\/home-v[12]$/, 'home'],
    [/^\/recruitments\/new$/, 'recruit_form'],
    [/^\/recruitments(\/region|\/welfare)?$/, 'recruit_list'],
    [/^\/recruitments\/[^/]+\/(applications|survey)(\/.*)?$/, 'clinic_recruitments'],
    [/^\/recruitments\/[^/]+$/, 'recruit_detail'],
    [/^\/clinic\/recruitments$/, 'clinic_recruitments'],
    [/^\/posts\/(new|my)$/, 'post_form'],
    [/^\/posts\/[^/]+\/edit$/, 'post_form'],
    [/^\/posts$/, 'post_list'],
    [/^\/posts\/[^/]+(\/comments\/[^/]+)?$/, 'post_detail'],
    [/^\/seminars\/[^/]+\/payment\/success$/, 'seminar_pay_done'],
    [/^\/seminars$/, 'seminar_list'],
    [/^\/seminars\/(new|history)$/, 'other'],
    [/^\/seminars\/[^/]+$/, 'seminar_detail'],
    [/^\/expert-lounge$/, 'expert_list'],
    [/^\/expert-lounge\/new$/, 'other'],
    [/^\/expert-lounge\/[^/]+\/edit$/, 'other'],
    [/^\/expert-lounge\/[^/]+(\/comments\/[^/]+)?$/, 'expert_detail'],
    [/^\/notices$/, 'notice_list'],
    [/^\/notices\/[^/]+$/, 'notice_detail'],
    [/^\/search$/, 'search'],
    [/^\/ai(\/.*)?$/, 'ai_chat'],
    [/^\/profile(\/.*)?$/, 'my_page'],
    [/^\/resume(\/.*)?$/, 'resume'],
    [/^\/apply\/history$/, 'apply_history'],
    [/^\/(threads|applications\/[^/]+\/thread)$/, 'threads'],
    [/^\/notifications$/, 'notifications'],
    [/^\/salary-surveys(\/.*)?$/, 'salary_survey'],
    [/^\/login(\/.*)?$/, 'login'],
    [/^\/signup(\/.*)?$/, 'signup'],
];

export function screenOfPath(pathname: string): UiScreen {
    const path = (pathname || '/').split(/[?#]/)[0].replace(/\/+$/, '') || '/';
    for (const [rule, screen] of SCREEN_RULES) {
        if (rule.test(path)) return screen;
    }
    return 'other';
}

/**
 * 상세 보기에 들어온 경로 — 방문 원장 상세 보기(RECRUITMENT_VIEW 등)의 `source`. 서버 `DetailEntrySource` 와 같은 값.
 * 기능 지표 '단계별 흐름'이 목록에서 들어온 상세 보기만 목록 다음 단계로 센다. 주소·검색어 원문은 남기지 않는다.
 */
export const DETAIL_SOURCE = {
    /** 같은 종류 목록에서(채용 목록 → 공고 상세 등) */
    LIST: 'list',
    HOME: 'home',
    /** 통합 검색에서 */
    SEARCH: 'search',
    /** 앱 푸시 알림을 눌러서 */
    PUSH: 'push',
    /** 회원이 공유한 주소로(utm_source=share) */
    SHARE: 'share',
    /** 바깥 링크로(다른 utm·다른 사이트) */
    LINK: 'link',
    /** 주소를 바로 열어서(새로 고침·즐겨찾기·출처 모름) */
    DIRECT: 'direct',
    /** 명치사 안 다른 화면에서(다른 상세·알림함 등) */
    OTHER: 'other',
} as const;

export type DetailSource = (typeof DETAIL_SOURCE)[keyof typeof DETAIL_SOURCE];

/** 상세 화면 → 같은 종류 목록 화면 */
const LIST_OF_DETAIL: Partial<Record<UiScreen, UiScreen>> = {
    recruit_detail: 'recruit_list',
    seminar_detail: 'seminar_list',
    expert_detail: 'expert_list',
    post_detail: 'post_list',
};

export interface DetailEntryContext {
    /** 지금 연 상세 경로 */
    detailPath: string;
    /** 이 문서 안에서 바로 앞 화면 경로 — 문서를 열고 처음 본 화면이면 null */
    previousPath: string | null;
    /** 방금 푸시 알림을 눌러 옮긴 화면인지 */
    fromPush: boolean;
    /** 첫 화면일 때 주소의 쿼리(utm 확인용) */
    landingSearch: string;
    /** document.referrer */
    referrer: string;
    /** 지금 사이트 origin */
    origin: string;
}

/** 상세 보기에 들어온 경로 */
export function detailEntrySource(context: DetailEntryContext): DetailSource {
    if (context.fromPush) return DETAIL_SOURCE.PUSH;
    if (context.previousPath == null) {
        let utmSource: string | null = null;
        try {
            utmSource = new URLSearchParams(context.landingSearch).get('utm_source');
        } catch { /* ignore */ }
        if (utmSource === 'share') return DETAIL_SOURCE.SHARE;
        if (utmSource) return DETAIL_SOURCE.LINK;
        let referrerOrigin: string | null = null;
        try {
            referrerOrigin = context.referrer ? new URL(context.referrer).origin : null;
        } catch { /* ignore */ }
        if (referrerOrigin && referrerOrigin !== context.origin) return DETAIL_SOURCE.LINK;
        return DETAIL_SOURCE.DIRECT;
    }
    const previous: UiScreen = screenOfPath(context.previousPath);
    if (previous === LIST_OF_DETAIL[screenOfPath(context.detailPath)]) return DETAIL_SOURCE.LIST;
    if (previous === 'home') return DETAIL_SOURCE.HOME;
    if (previous === 'search') return DETAIL_SOURCE.SEARCH;
    return DETAIL_SOURCE.OTHER;
}

/** 상세 화면 경로 → 기록 대상(공유 버튼 클릭용). 글·전문가 글만, 그 밖은 null */
export function detailTargetOfPath(pathname: string): { screen: UiScreen; kind: UiKind; id: string } | null {
    const post = pathname.match(/^\/posts\/([^/]+)$/);
    if (post && post[1] !== 'new' && post[1] !== 'my') return { screen: 'post_detail', kind: 'post', id: post[1] };
    const expert = pathname.match(/^\/expert-lounge\/([^/]+)$/);
    if (expert && expert[1] !== 'new') return { screen: 'expert_detail', kind: 'expert', id: expert[1] };
    return null;
}

/** 세미나 결제 금액 구간 — 원값은 보내지 않는다(서버도 이 값들만 받는다) */
export function amountBandOf(amount: number | null | undefined): string {
    const value = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
    if (value <= 0) return 'free';
    if (value < 30_000) return 'lt30k';
    if (value < 100_000) return '30k_100k';
    if (value < 300_000) return '100k_300k';
    return 'gte300k';
}

/** 카드·행에 붙이는 표시 — 공용 관찰자가 이 값으로 노출·클릭을 센다(`useUiScope`) */
export const UI_TARGET_ATTR = 'data-ui-target';
export const UI_POS_ATTR = 'data-ui-pos';
/** 섹션 "더보기" 같은 버튼 표시 */
export const UI_ACTION_ATTR = 'data-ui-action';

/** 카드 루트에 펼쳐 넣을 속성 — `<Card {...uiTargetAttrs(UI_KIND.POST, id, index)} />` */
export function uiTargetAttrs(kind: UiKind, id: string | number | null | undefined, pos?: number): Record<string, string> {
    if (id === null || id === undefined || id === '') return {};
    const attrs: Record<string, string> = { [UI_TARGET_ATTR]: `${kind}:${id}` };
    if (typeof pos === 'number') attrs[UI_POS_ATTR] = String(pos);
    return attrs;
}
